import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UsuarioRepository } from 'src/app/domain/service/usuario/usuario-repository';
import {
  UsuarioSesion
} from '@model/usuario/usuario-sesion';
import { getDniAutenticado } from 'src/app/common/auth-utils';

/**
 * Adaptador HTTP: HttpUsuarioRepository
 *
 * Implementa el puerto UsuarioRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (UsuarioController).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto UsuarioRepository del dominio
 *
 * Backend: 580-girop_servicios-ms (puerto 8080)
 * Endpoint: /api/servicios/usuario
 *
 * @legacy PrestacionServiciosCGRHandler.sessionData()
 * @author Sura - Migración GWT a Spring Boot
 * @since 1.0
 */
@Injectable({ providedIn: 'root' })
export class HttpUsuarioRepository implements UsuarioRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/usuario`;

  /**
   * Obtiene el DNI del usuario autenticado via SSO.
   *
   * @returns DNI del usuario (tipo + número, ej: "C43750219")
   * @throws Error si no hay sesión SSO activa
   */
  private getDni(): string {
    return getDniAutenticado();
  }

  /**
   * Construye los headers HTTP con el DNI del usuario.
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'X-User-DNI': this.getDni()
    });
  }

  /**
   * Obtiene los datos de sesión del usuario actual.
   *
   * GET /api/servicios/usuario/sesion
   * Header: X-User-DNI
   */
  async obtenerDatosSesion(): Promise<UsuarioSesion> {
    if (!environment.production) {
      console.log('[UsuarioRepository] Obteniendo datos de sesión');
    }

    return firstValueFrom(
      this.http.get<UsuarioSesion>(`${this.baseUrl}/sesion`, {
        headers: this.getHeaders()
      })
    );
  }

  /**
   * Verifica si el usuario está autenticado y activo.
   *
   * GET /api/servicios/usuario/verificar
   */
  async verificarUsuario(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ valido: boolean; mensaje: string }>(`${this.baseUrl}/verificar`, {
          headers: this.getHeaders()
        })
      );
      return response.valido;
    } catch (error) {
      console.error('[UsuarioRepository] Error verificando usuario:', error);
      return false;
    }
  }

}
